import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import {
  Dashboard,
  People,
  EventAvailable,
  Payments,
  Work,
  RocketLaunch,
  TrendingUp,
  School,
  Business,
  Settings,
  Chat as ChatIcon,
  Assignment,
  FolderSpecial,
  Article,
  Security,
  Domain,
  Badge,
  AccountTree,
  HowToReg,
  AccessTime,
  CalendarMonth,
  Handshake,
  Timeline,
  Receipt,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logout } from "../../store/slices/authSlice";
import {
  toggleSidebar,
  setSidebarOpen,
  showSnackbar,
} from "../../store/slices/uiSlice";
import {
  clearNotificationsAsync,
  fetchRolesAsync,
} from "../../store/slices/dataSlice";
import {
  receiveMessage,
  markMessagesReadAsync,
  fetchChatContacts,
  removeMessage,
} from "../../store/slices/chatSlice";
import { useSocket } from "../../context/SocketContext";
import { ROLES_CONFIG } from "../../types";
import { apiClient } from "../../api/apiClient";
import Sidebar from "../organisms/Sidebar";
import Topbar from "../organisms/Topbar";
import { checkPermission } from "../../utils/permissions";

const NAV_META: Record<string, { icon: React.ReactNode; label: string }> = {
  dashboard: { icon: <Dashboard />, label: "Dashboard" },
  tasks: { icon: <Assignment />, label: "Tasks" },
  projects: { icon: <FolderSpecial />, label: "Projects" },
  timesheets: { icon: <AccessTime />, label: "Timesheets" },
  chat: { icon: <ChatIcon />, label: "Chat" },
  employees: { icon: <People />, label: "Employees" },
  attendance: { icon: <HowToReg />, label: "Attendance" },
  leave: { icon: <EventAvailable />, label: "Leave" },
  payroll: { icon: <Payments />, label: "Payroll" },
  recruitment: { icon: <Work />, label: "Recruitment" },
  onboarding: { icon: <RocketLaunch />, label: "Onboarding" },
  performance: { icon: <TrendingUp />, label: "Performance" },
  training: { icon: <School />, label: "Training" },
  departments: { icon: <Domain />, label: "Departments" },
  designations: { icon: <Badge />, label: "Designations" },
  hierarchy: { icon: <AccountTree />, label: "Hierarchy" },
  reports: { icon: <Article />, label: "Reports" },
  settings: { icon: <Settings />, label: "Settings" },
  roles: { icon: <Security />, label: "Roles" },
  companies: { icon: <Business />, label: "All Companies" },
  mycompany: { icon: <Business />, label: "My Company" },
  clients: { icon: <Handshake />, label: "Clients" },
  invoices: { icon: <Receipt />, label: "Invoices" },
  tracking: { icon: <Timeline />, label: "Employee Tracking" },
  shifts: { icon: <CalendarMonth />, label: "Shift Management" },
  "company-documents": {
    icon: <Article />,
    label: "Company Documents",
  },
  myprofile: { icon: <People />, label: "My Profile" },
};

const GROUP_META: Record<string, string> = {
  dashboard: "Overview",
  reports: "Overview",

  companies: "Organization",
  mycompany: "Organization",
  departments: "Organization",
  designations: "Organization",
  hierarchy: "Organization",
  roles: "Organization",

  employees: "People",
  attendance: "People",
  tracking: "People",
  shifts: "People",
  leave: "People",
  timesheets: "People",

  recruitment: "Recruitment & Growth",
  onboarding: "Recruitment & Growth",
  performance: "Recruitment & Growth",
  training: "Recruitment & Growth",

  projects: "Work",
  tasks: "Work",
  clients: "Work",
  "company-documents": "Work",

  payroll: "Finance",
  invoices: "Finance",

  chat: "Communication & System",
  settings: "Communication & System",
};

const Shell: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const user = useAppSelector((s) => s.auth.user);
  const employees = useAppSelector((s) => s.data.employees);
  const sidebarOpen = useAppSelector((s) => s.ui.sidebarOpen);
  const notifications = useAppSelector((s) => s.data.notifications);
  const leaveRequests = useAppSelector((s) => s.data.leaveRequests) || [];

  const dbRoles = useAppSelector((s) => s.data.roles) || [];

  const activeContactId = useAppSelector(
    (s) => s.chat?.activeContactId,
  );

  const chatContacts = useAppSelector(
    (s) => s.chat?.contacts || [],
  );

  const totalUnreadChat = chatContacts.reduce(
    (acc: any, c: any) => acc + (c.unread || 0),
    0,
  );

  const { socket } = useSocket();

  const safeTrim = (s?: string) =>
    (s || "").toLowerCase().trim();

  const safeLocal = (s?: string) =>
    safeTrim(s)
      .split("@")[0]
      .replace(/[^a-z0-9]/g, "");

  const matchEmployee = (e: any, u: any) => {
    if (!u || !e) return false;

    if (e.user === u._id || e.user?._id === u._id) {
      return true;
    }

    if (e.id === u.empId || e._id === u.empId) {
      return true;
    }

    const eEmail = safeTrim(e.email);
    const uEmail = safeTrim(u.email);

    if (eEmail && uEmail && eEmail === uEmail) {
      return true;
    }

    const eLocal = safeLocal(e.email);
    const uLocal = safeLocal(u.email);
    const uUser = safeLocal(u.username);
    const uName = safeLocal(u.name);
    const eName = safeLocal(e.name);

    if (eLocal && uLocal && eLocal === uLocal) {
      return true;
    }

    if (eLocal && uUser && eLocal === uUser) {
      return true;
    }

    if (uLocal && eName && uLocal === eName) {
      return true;
    }

    if (eName && uName && eName === uName) {
      return true;
    }

    if (eName && uUser && eName === uUser) {
      return true;
    }

    return false;
  };

  const empProfile = employees.find((e: any) =>
    matchEmployee(e, user),
  );

  // Safe role name from user
  const userRoleStr = user?.role
    ? user.role.trim().toLowerCase()
    : "";

  const sysRoleStr = empProfile?.sysRole
    ? empProfile.sysRole.trim().toLowerCase()
    : "";

  // Strictly check permissions from live Redux roles first
  const myRolePerm = dbRoles.find((r: any) => {
    if (!r.name) return false;

    const rName = r.name.trim().toLowerCase();
    const rLabel = r.label
      ? r.label.trim().toLowerCase()
      : rName;

    return (
      rName === userRoleStr ||
      rLabel === userRoleStr ||
      rName === sysRoleStr ||
      rLabel === sysRoleStr ||
      (user?.role &&
        typeof user.role === "string" &&
        (r._id === user.role || r.id === user.role)) ||
      (user?.role &&
        typeof user.role === "object" &&
        (r._id === (user.role as any)._id ||
          r.id === (user.role as any).id ||
          rName ===
            ((user.role as any).name || "")
              .trim()
              .toLowerCase())) ||
      (empProfile?.sysRole &&
        (r._id === empProfile.sysRole ||
          r.id === empProfile.sysRole)) ||
      (empProfile?.role &&
        (r._id === empProfile.role ||
          r.id === empProfile.role))
    );
  });

  const isMasterAdminUser =
    user?.role?.toLowerCase() === "super admin" ||
    user?.role?.toLowerCase() === "master admin" ||
    empProfile?.role?.toLowerCase() === "super admin" ||
    empProfile?.role?.toLowerCase() === "master admin" ||
    empProfile?.sysRole?.toLowerCase() === "super admin" ||
    empProfile?.sysRole?.toLowerCase() === "master admin";

  const isAdmin =
    user?.role?.toLowerCase() === "admin" ||
    isMasterAdminUser;

  const leavePerm = myRolePerm?.permissions?.find(
    (p: any) => p.module === "leave",
  );

  const canApproveLeave =
    isAdmin || (leavePerm?.canApprove ?? false);

  const roleConfig = user
    ? ROLES_CONFIG[
        user.role.toLowerCase() as keyof typeof ROLES_CONFIG
      ] || {
        label: isMasterAdminUser
          ? "Super Admin"
          : myRolePerm?.label || user.role,
        color: myRolePerm?.color || "blue",
      }
    : null;

  const pendingLeavesCount = leaveRequests.filter(
    (l: any) =>
      l.status === "Pending" &&
      l.type !== "WFH" &&
      l.leaveType !== "WFH",
  ).length;

  const [dynamicHistory, setDynamicHistory] = useState<any[]>(
    () => {
      try {
        const stored = localStorage.getItem(
          `hrms_dynamic_notifs_${
            user?._id || "default"
          }`,
        );

        return stored ? JSON.parse(stored) : [];
      } catch {
        return [];
      }
    },
  );

  useEffect(() => {
    setDynamicHistory((prev) => {
      let changed = false;
      let newHistory = [...prev];

      if (pendingLeavesCount > 0) {
        const typeId = `pending_leaves_${pendingLeavesCount}`;

        if (
          !newHistory.find(
            (n) => (n.typeId || n.id) === typeId,
          )
        ) {
          newHistory = newHistory.filter(
            (n) => n.type !== "leave",
          );

          newHistory.unshift({
            id: `${typeId}_${Date.now()}_${Math.random()
              .toString(36)
              .substr(2, 5)}`,
            typeId,
            type: "leave",
            message: canApproveLeave
              ? `You have ${pendingLeavesCount} pending leave request(s) to review.`
              : `You have ${pendingLeavesCount} leave request(s) pending approval.`,
            time: "Just now",
            read: false,
          });

          changed = true;
        }
      } else {
        const beforeLen = newHistory.length;

        newHistory = newHistory.filter(
          (n) => n.type !== "leave",
        );

        if (newHistory.length !== beforeLen) {
          changed = true;
        }
      }

      chatContacts.forEach((c: any) => {
        if (c.unread > 0) {
          const typeId = `chat_unread_${c.id}_${c.unread}`;

          if (
            !newHistory.find(
              (n) => (n.typeId || n.id) === typeId,
            )
          ) {
            newHistory = newHistory.filter(
              (n) =>
                !(
                  n.type === "chat" &&
                  (n.typeId || n.id).startsWith(
                    `chat_unread_${c.id}_`,
                  )
                ),
            );

            newHistory.unshift({
              id: `${typeId}_${Date.now()}_${Math.random()
                .toString(36)
                .substr(2, 5)}`,
              typeId,
              type: "chat",
              message: `You have ${c.unread} unread message(s) from ${c.name}`,
              time: "Just now",
              read: false,
            });

            changed = true;
          }
        } else {
          const beforeLen = newHistory.length;

          newHistory = newHistory.filter(
            (n) =>
              !(
                n.type === "chat" &&
                (n.typeId || n.id).startsWith(
                  `chat_unread_${c.id}_`,
                )
              ),
          );

          if (newHistory.length !== beforeLen) {
            changed = true;
          }
        }
      });

      if (changed) {
        localStorage.setItem(
          `hrms_dynamic_notifs_${
            user?._id || "default"
          }`,
          JSON.stringify(newHistory),
        );

        return newHistory;
      }

      return prev;
    });
  }, [
    pendingLeavesCount,
    chatContacts,
    canApproveLeave,
    user?._id,
  ]);

  const [seenIds, setSeenIds] = useState<Set<string>>(
    () => {
      try {
        const stored = localStorage.getItem(
          `hrms_seen_notif_ids_${
            user?._id || "default"
          }`,
        );

        return stored
          ? new Set(JSON.parse(stored))
          : new Set();
      } catch {
        return new Set();
      }
    },
  );

  const allNotifications = [
    ...dynamicHistory,
    ...notifications,
  ].map((n) => ({
    ...n,
    read: n.read || seenIds.has(n.id),
  }));

  const unseenCount = allNotifications.filter(
    (n) => !n.read,
  ).length;

  const formatFallback = (name?: string) => {
    if (!name) return "User";

    const clean = name.includes("@")
      ? name.split("@")[0]
      : name;

    return clean
      .split(/[\.\-_]/)
      .map(
        (w) =>
          w.charAt(0).toUpperCase() + w.slice(1),
      )
      .join(" ");
  };

  const displayName =
    empProfile?.name ||
    formatFallback(user?.name || user?.username);

  const getInitials = (name: string) => {
    if (!name) return "U";

    const cleanName = name.split("@")[0];

    const parts = cleanName
      .split(/[\s.]+/)
      .filter(Boolean);

    if (parts.length >= 2) {
      return (
        parts[0][0] + parts[1][0]
      ).toUpperCase();
    }

    return cleanName.substring(0, 2).toUpperCase();
  };

  const displayInitials =
    empProfile?.initials || getInitials(displayName);

  const displayColor =
    empProfile?.avColor || "blue";

  const displayDept = isMasterAdminUser
    ? "Super Admin"
    : empProfile?.role &&
        empProfile.role !== "N/A" &&
        empProfile.role !== ""
      ? empProfile.role
      : roleConfig?.label ||
        user?.role ||
        "Employee";

  const [notifAnchor, setNotifAnchor] =
    useState<null | HTMLElement>(null);

  const [userAnchor, setUserAnchor] =
    useState<null | HTMLElement>(null);

  const handleOpenNotifs = async (
    e: React.MouseEvent<HTMLElement>,
  ) => {
    setNotifAnchor(e.currentTarget);
  };

  const handleMarkAllRead = async () => {
    const allIds = allNotifications.map(
      (n) => n.id,
    );

    const newSeenIds = new Set([
      ...seenIds,
      ...allIds,
    ]);

    setSeenIds(newSeenIds);

    try {
      localStorage.setItem(
        `hrms_seen_notif_ids_${
          user?._id || "default"
        }`,
        JSON.stringify([...newSeenIds]),
      );

      await apiClient.put(
        "/notifications/read-all",
      );
    } catch {}
  };

  const handleClearNotifs = async () => {
    try {
      setDynamicHistory([]);

      localStorage.removeItem(
        `hrms_dynamic_notifs_${
          user?._id || "default"
        }`,
      );

      await dispatch(clearNotificationsAsync());
    } catch {}
  };

  useEffect(() => {
    if (isMobile) {
      dispatch(setSidebarOpen(false));
    } else {
      dispatch(setSidebarOpen(true));
    }
  }, [isMobile, dispatch]);

  // Global Chat Data Initialization
  useEffect(() => {
    dispatch(fetchChatContacts());
    dispatch(fetchRolesAsync());
  }, [dispatch]);

  // Global Chat Listener
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (m: any) => {
      const senderId = m.sender?._id || m.sender;
      const isMe = senderId === user?._id;
      const contactId = m.groupId
        ? m.groupId
        : senderId;

      dispatch(
        receiveMessage({
          contactId,
          text: m.text,
          time: new Date(
            m.createdAt,
          ).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          }),
          fileUrl: m.fileUrl,
          fileName: m.fileName,
          fileType: m.fileType,
          incrementUnread:
            !isMe &&
            (location.pathname !== "/app/chat" ||
              contactId !== activeContactId),
        }),
      );

      if (
        !isMe &&
        (location.pathname !== "/app/chat" ||
          contactId !== activeContactId)
      ) {
        const contact = chatContacts.find(
          (c: any) => c.id === contactId,
        );

        const senderName = contact
          ? contact.name
          : "Someone";

        const preview = m.text
          ? m.text.length > 30
            ? m.text.substring(0, 30) + "..."
            : m.text
          : m.fileName || "a file";

        dispatch(
          showSnackbar({
            message: m.groupId
              ? `New message in ${senderName}: ${preview}`
              : `New message from ${senderName}: ${preview}`,
            severity: "info",
          }),
        );
      } else if (
        !isMe &&
        location.pathname === "/app/chat" &&
        contactId === activeContactId
      ) {
        dispatch(
          markMessagesReadAsync(activeContactId),
        );
      }
    };

    const handleMessageDeleted = (
      messageId: string,
    ) => {
      dispatch(removeMessage(messageId));
    };

    socket.on(
      "new-message",
      handleNewMessage,
    );

    socket.on(
      "message-deleted",
      handleMessageDeleted,
    );

    return () => {
      socket.off(
        "new-message",
        handleNewMessage,
      );

      socket.off(
        "message-deleted",
        handleMessageDeleted,
      );
    };
  }, [
    socket,
    dispatch,
    activeContactId,
    location.pathname,
    chatContacts,
    user?._id,
  ]);

  useEffect(() => {
    if (
      socket &&
      chatContacts &&
      chatContacts.length > 0
    ) {
      const groupIds = chatContacts
        .filter((c: any) => c.isGroup)
        .map((c: any) => c.id);

      if (groupIds.length > 0) {
        socket.emit(
          "join-group-rooms",
          groupIds,
        );
      }
    }
  }, [socket, chatContacts]);

  // Super Admin check
  const isMasterAdmin =
    empProfile?.role?.toLowerCase() ===
      "super admin" ||
    empProfile?.role?.toLowerCase() ===
      "master admin" ||
    isMasterAdminUser;

  // Whether role/permission data has finished loading
  const rolesDataLoaded =
    dbRoles.length > 0 || !!myRolePerm;

  /*
   * IMPORTANT:
   * Keep the NAV_META order as the priority order.
   *
   * This means:
   * Dashboard -> Tasks -> Projects -> Timesheets -> ...
   *
   * However, only pages with permission are added to finalNavKeys.
   *
   * When a restricted page is opened directly,
   * the first page from this same navigation order
   * that the user CAN access will be selected.
   */
  const navOrder = Object.keys(NAV_META);

  const finalNavKeys = navOrder.filter((k) => {
    // Roles management is only for administrators
    if (k === "roles" && !isAdmin) {
      return false;
    }

    // All Companies is only for Master Admin
    if (k === "companies" && !isMasterAdmin) {
      return false;
    }

    // My Company should not be shown to Master Admin
    if (k === "mycompany" && isMasterAdmin) {
      return false;
    }

    // Master Admin has access to everything
    if (isMasterAdmin) {
      return true;
    }

    // Admin has access to everything except explicitly restricted
    if (isAdmin) {
      return true;
    }

    /*
     * checkPermission() is the single source of truth for module
     * access. There is intentionally no legacy `nav` array fallback
     * here anymore — a module with no explicit permission entry for
     * a role is simply NOT accessible, instead of silently falling
     * back to a stale/leftover `nav` array on the role or user
     * object. This prevents modules (e.g. "tasks") from becoming
     * accidentally accessible just because an old `nav` array still
     * lists them.
     */
    const hasAccess = checkPermission(
      user,
      dbRoles,
      k,
      "canView",
      employees,
    );

    /*
     * Only show Dashboard temporarily while
     * permissions are still loading.
     */
    if (
      !hasAccess &&
      k === "dashboard" &&
      !rolesDataLoaded
    ) {
      return true;
    }

    return hasAccess;
  });

  const GROUP_ORDER = [
    "Overview",
    "Organization",
    "People",
    "Recruitment & Growth",
    "Work",
    "Finance",
    "Communication & System",
    "Other",
  ];

  const flatNavItems = finalNavKeys.map(
    (k: string) => ({
      key: k,
      ...NAV_META[k],
      group: GROUP_META[k] || "Other",
    }),
  );

  const groupedNavItemsObj =
    flatNavItems.reduce(
      (acc, item) => {
        if (!acc[item.group]) {
          acc[item.group] = [];
        }

        acc[item.group].push(item);

        return acc;
      },
      {} as Record<string, any[]>,
    );

  /*
   * Keep the original NAV_META order.
   *
   * Do NOT alphabetically sort the pages.
   *
   * This makes the first permitted page predictable.
   */
  const groupedNavItems = Object.keys(
    groupedNavItemsObj,
  )
    .sort((a, b) => {
      const aIdx =
        GROUP_ORDER.indexOf(a);

      const bIdx =
        GROUP_ORDER.indexOf(b);

      return (
        (aIdx === -1 ? 99 : aIdx) -
        (bIdx === -1 ? 99 : bIdx)
      );
    })
    .map((groupName) => {
      const items =
        groupedNavItemsObj[groupName];

      items.sort(
        (a, b) =>
          navOrder.indexOf(a.key) -
          navOrder.indexOf(b.key),
      );

      return {
        group: groupName,
        items,
      };
    });

  /*
   * Get current page from the FIRST path segment
   * after /app/.
   *
   * Example:
   * /app/reports/custom-builder
   * -> reports
   *
   * /app/companies/123
   * -> companies
   */
  const pathAfterApp =
    location.pathname.replace(
      /^\/app\/?/,
      "",
    );

  const currentPage =
    pathAfterApp.split("/")[0] ||
    "dashboard";

  const pageTitle =
    NAV_META[currentPage]?.label ||
    "Dashboard";

  /*
   * Pages that should always be accessible.
   */
  const ALWAYS_ALLOWED_PAGES = new Set([
    "myprofile",
  ]);

  /*
   * ============================================================
   * FIXED ROUTE GUARD
   * ============================================================
   *
   * If the current page is not allowed:
   *
   * 1. Find the FIRST allowed page according to NAV_META order.
   * 2. Navigate there.
   * 3. If no page is allowed, go to My Profile.
   *
   * This prevents an unauthorized page from showing.
   *
   * Example:
   *
   * Dashboard   ❌
   * Tasks       ❌
   * Projects    ❌
   * Employees   ❌
   * Leave       ✅
   *
   * User opens /app/dashboard
   *
   * Result:
   *
   * /app/dashboard
   *       ↓
   * permission denied
   *       ↓
   * /app/leave
   */
  useEffect(() => {
    if (!rolesDataLoaded) {
      return;
    }

    // My Profile is always allowed
    if (
      ALWAYS_ALLOWED_PAGES.has(
        currentPage,
      )
    ) {
      return;
    }

    // Current page is allowed
    if (
      finalNavKeys.includes(
        currentPage,
      )
    ) {
      return;
    }

    /*
     * IMPORTANT:
     * finalNavKeys is already ordered according
     * to NAV_META.
     *
     * Therefore finalNavKeys[0] is the FIRST
     * permitted module.
     */
    const firstAllowedPage =
      finalNavKeys.find(
        (key) =>
          key !== currentPage,
      );

    if (firstAllowedPage) {
      navigate(
        `/app/${firstAllowedPage}`,
        {
          replace: true,
        },
      );

      return;
    }

    /*
     * If there is no permitted module,
     * My Profile is the safe fallback.
     */
    navigate(
      "/app/myprofile",
      {
        replace: true,
      },
    );
  }, [
    rolesDataLoaded,
    currentPage,
    finalNavKeys,
    navigate,
  ]);

  const handleNav = (key: string) => {
    navigate(`/app/${key}`);

    if (isMobile) {
      dispatch(
        setSidebarOpen(false),
      );
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  /*
   * Don't render restricted page content.
   * The route guard above will redirect to the
   * first allowed page.
   */
  const canSeeCurrentPage =
    ALWAYS_ALLOWED_PAGES.has(
      currentPage,
    ) ||
    finalNavKeys.includes(
      currentPage,
    );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <Sidebar
        isMobile={isMobile}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={(val) =>
          dispatch(
            setSidebarOpen(val),
          )
        }
        roleConfig={roleConfig}
        navItems={groupedNavItems}
        currentPage={currentPage}
        handleNav={handleNav}
        handleLogout={handleLogout}
        user={user}
        displayName={displayName}
        displayDept={displayDept}
        displayInitials={
          displayInitials
        }
        displayColor={displayColor}
        pendingLeavesCount={
          pendingLeavesCount
        }
        totalUnreadChat={
          totalUnreadChat
        }
        profilePic={
          empProfile?.profilePic
        }
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <Topbar
          pageTitle={pageTitle}
          sidebarOpen={sidebarOpen}
          toggleSidebar={() =>
            dispatch(
              toggleSidebar(),
            )
          }
          notifAnchor={notifAnchor}
          setNotifAnchor={
            setNotifAnchor
          }
          userAnchor={userAnchor}
          setUserAnchor={
            setUserAnchor
          }
          unseenCount={unseenCount}
          allNotifications={
            allNotifications
          }
          handleOpenNotifs={
            handleOpenNotifs
          }
          handleMarkAllRead={
            handleMarkAllRead
          }
          handleClearNotifs={
            handleClearNotifs
          }
          handleLogout={() =>
            dispatch(logout())
          }
          handleNav={handleNav}
          user={user}
          displayName={displayName}
          displayInitials={
            displayInitials
          }
          displayColor={
            displayColor
          }
          roleConfig={roleConfig}
          profilePic={
            empProfile?.profilePic
          }
        />

        {/* Page content */}
        <Box
          sx={{
            flex: 1,
            background: "#f8fafc",
            p: {
              xs: 2,
              sm: 3,
            },
            overflowX: "hidden",
          }}
        >
          {rolesDataLoaded &&
          !canSeeCurrentPage ? null : (
            <Outlet />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Shell;